import { useCallback, useEffect, useState } from 'react';

import { useMapsLibrary } from '@vis.gl/react-google-maps';

interface UsePlacesAutoComplete {
  onPlaceSelect: OnPlaceSelect;
  /**
   * Optional callback to format the input value when a place is selected
   * If not provided, defaults to using formatted_address
   */
  formatSelectedPlace?: (place: google.maps.places.Place) => string;
}
type OnPlaceSelect = (place: google.maps.places.Place | null) => void;

const usePlacesAutoComplete = ({
  onPlaceSelect,
  formatSelectedPlace = (place) => place.formattedAddress ?? '',
}: UsePlacesAutoComplete) => {
  const places = useMapsLibrary('places');

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteSessionToken
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();

  const [predictionResults, setPredictionResults] = useState<
    Array<google.maps.places.AutocompleteSuggestion>
  >([]);

  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    if (!places) return;

    setSessionToken(new places.AutocompleteSessionToken());

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only instantiate when places is updated
  }, [places]);

  const fetchPredictions = useCallback(
    async (inputValue: string) => {
      if (!inputValue) {
        setPredictionResults([]);
        return;
      }

      try {
        const request = { input: inputValue, sessionToken };
        const response = await places?.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        setPredictionResults(response?.suggestions ?? []);
      } catch (error) {
        setPredictionResults([]);
        console.error('Error fetching autocomplete suggestions:', error);
      }
    },
    [sessionToken, places]
  );

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places) return;

      const detailsRequestCallback = (placeDetails: google.maps.places.Place | null) => {
        onPlaceSelect(placeDetails);
        setPredictionResults([]);
        setInputValue(placeDetails ? formatSelectedPlace(placeDetails) : '');
        setSessionToken(new places.AutocompleteSessionToken());
      };

      const placePrediction = suggestion.placePrediction;

      if (!placePrediction) {
        detailsRequestCallback(null);
        return;
      }

      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ['displayName', 'formattedAddress', 'addressComponents', 'location'],
        });
        detailsRequestCallback(place);
      } catch (error) {
        console.error('Error fetching place details:', error);
        detailsRequestCallback(null);
      }
    },
    [onPlaceSelect, places, formatSelectedPlace]
  );

  const onInputChange = useCallback(
    async (value = '') => {
      setInputValue(value);
      await fetchPredictions(value);
    },
    [fetchPredictions]
  );

  return { onInputChange, predictionResults, handleSuggestionClick, inputValue };
};

export { usePlacesAutoComplete };
export type { OnPlaceSelect };
